import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Apiservice from '../services/Apiservice';
import Navigationbar from '../components/Navigationbar';
import Footer from '../components/Footer';
import CommonFunctions from '../components/CommonFunctions';
import { Helmet } from 'react-helmet';

const BlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupedPosts, setGroupedPosts] = useState({}); // For grouping by year

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await Apiservice.getBlogPosts();
        const data = response.data;
        setFeaturedPosts(data.featured_posts);
        setPosts(data.posts);
      } catch (error) {
        console.error('Error fetching posts:', error);
        setError('Failed to load posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Group posts by year when they are fetched
    const groupByYear = (posts) => {
      return posts.reduce((acc, post) => {
        const year = new Date(post.created_at).getFullYear();
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(post);
        return acc;
      }, {});
    };

    if (posts.length > 0) {
      setGroupedPosts(groupByYear(posts));
    }
  }, [posts]);

  if (loading) return <div className="progress" style={{height: "4px"}}>
  <div 
    className="progress-bar progress-bar-animated" 
    role="progressbar" 
    style={{ width: '100%' }} 
    aria-valuenow={100} 
    aria-valuemin={0} 
    aria-valuemax={100}>
  </div>
</div>;
  if (error) return <div>{error}</div>;

  return (
    <React.Fragment>
    <Helmet>
      <title>Cricket Latest News & Updates | WPL 2025 & IPL 2025 | WPL Cricinfo</title>
      <meta
        name="description"
        content="Get the latest cricket news and updates for WPL 2025 and IPL 2025, including match results, player highlights, and more from WPL Cricinfo."
      />
      <meta
        name="keywords"
        content="WPL 2025, IPL 2025, cricket news, WPL Cricinfo, match updates, player highlights"
      />
      <meta property="og:title" content="Cricket Latest News & Updates | WPL 2025 & IPL 2025 | WPL Cricinfo" />
      <meta
        property="og:description"
        content="Stay updated with the latest cricket news for WPL 2025 and IPL 2025, featuring match results, player highlights, and more."
      />
      <meta property="og:image" content="https://wplcricinfo.com/wpl/admin/uploads/default-news-image.jpg" />
      <meta property="og:url" content={window.location.href} />
      <meta property="og:type" content="website" />
    </Helmet>      
      <div className="bg-fluid"></div>
      <Navigationbar />
      <div className='container-fluid'>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/" className="text-muted text-decoration-none">Home</Link></li>
            <li className="breadcrumb-item text-white active" aria-current="page">WPL News & Updates</li>
          </ol>
        </nav>

        <div className='row'>
          <div className='col-md-12'>
            <h1 className='fs-5 text-white mb-3'>Cricket Latest News & Updates, IPL2025, WPL2025</h1>
          </div>
          <div className='col-md-12'>
            <div className='row mb-4'>
              {featuredPosts.length > 0 && (
                <React.Fragment>
                  {featuredPosts.map((featuredPost) => (
                    <div className='col-md-6' key={featuredPost.id}>
                      <div className="card h-100 shadow">
                        <img src={`https://wplcricinfo.com/wpl/admin/${featuredPost.imgUrl}`} alt={`Featured Post: ${featuredPost.title}`} />
                        <div className="card-body">
                          <h2 className="card-title fs-6">{featuredPost.title}</h2>
                          <p className="card-text"><strong>Tournament:</strong> {featuredPost.tournament_type || 'N/A'}</p>
                        </div>
                        <div className='card-footer'><Link to={`/post/${featuredPost.id}/${CommonFunctions.formatPostTitleForLink(featuredPost.title)}`}>Read More...</Link></div>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              )}
            </div>
          </div>
        </div>

        {/* Bootstrap Tabs for Year-wise Posts */}
        <ul className="nav nav-tabs" id="myTab" role="tablist">
          {Object.keys(groupedPosts).sort((a, b) => b - a).map((year, index) => (
            <li key={year} className="nav-item" role="presentation">
              <button 
                className={`nav-link ${index === 0 ? 'active' : ''}`} 
                id={`tab-${year}-tab`} 
                data-bs-toggle="tab" 
                data-bs-target={`#tab-${year}`} 
                type="button" 
                role="tab" 
                aria-controls={`tab-${year}`} 
                aria-selected={index === 0}
              >
                {year}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content" id="myTabContent">
          {Object.keys(groupedPosts).sort((a, b) => b - a).map((year, index) => (
            <div 
              key={year} 
              className={`tab-pane fade ${index === 0 ? 'show active' : ''}`} 
              id={`tab-${year}`} 
              role="tabpanel" 
              aria-labelledby={`tab-${year}-tab`}
            >
              <div className='row'>
                {groupedPosts[year].map(post => (
                  <div className='col-md-4 mb-3' key={post.id}>
                    <div className="card h-100 shadow">
                      <img src={`https://wplcricinfo.com/wpl/admin/${post.imgUrl}`} alt={`Post: ${post.title}`} className='img-fluid' />
                      <div className='card-body'>
                        <h3 className='card-title fs-6'>{post.title}</h3>
                        {post.tournament_type && ( <p className="card-text"><strong>Tournament:</strong> {post.tournament_type || 'N/A'}</p>)}
                        <Link to={`/post/${post.id}/${CommonFunctions.formatPostTitleForLink(post.title)}`}>Read More...</Link>
                      </div>
                      <div className='card-footer small text-muted text-end'>
                        Date Posted on: {CommonFunctions.getPostDateFormat(post.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default BlogPosts;